import React from 'react';
import { CheckCircle, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Login Page - Skilling in a Box style design
 * Only SSO (LevelUp) login option visible
 */
function Login({ onLogin }) {
  const { t } = useTranslation();

  const handleSSOLogin = () => {
    const levelupUrl = process.env.REACT_APP_LEVELUP_URL || 'https://skillupwithlevelup.com';
    const ssoLoginUrl = `${levelupUrl}/login?redirect_back=compete`;
    window.location.href = ssoLoginUrl;
  };

  const handleRegister = () => {
    // LevelUp registration with referral code for Compete portal
    const levelupUrl = process.env.REACT_APP_LEVELUP_URL || 'https://skillupwithlevelup.com';
    window.location.href = `${levelupUrl}/register?ref=compete`;
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <header style={{ 
        backgroundColor: '#1B5E5F', 
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        padding: '16px 60px',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src="/technofocus-logo.png" alt="Technofocus" style={{ height: '20px', width: 'auto', filter: 'brightness(0) invert(1)' }} />
          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '20px', fontWeight: '200' }}>|</span>
          <img src="/levelup-logo-light.svg" alt="LevelUp" style={{ height: '20px', width: 'auto', marginTop: '7px' }} />
          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '20px', fontWeight: '200' }}>|</span>
          <img src="/ms-levelup-logo.svg" alt="Sponsored by Microsoft" style={{ height: '28px', width: 'auto' }} />
        </div>
      </header>

      {/* Main Content - Split Layout */}
      <main style={{ 
        flex: 1, 
        display: 'flex',
        minHeight: 0
      }}>
        {/* Left Panel - Branding & Info */}
        <div style={{ 
          flex: '1 1 50%',
          backgroundColor: '#1B5E5F',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '60px',
          position: 'relative'
        }}>
          <div style={{ position: 'relative', zIndex: 1, maxWidth: '500px' }}>
            {/* Title */}
            <h1 style={{ 
              fontSize: '44px', 
              fontWeight: '700', 
              color: '#FFFFFF',
              marginBottom: '24px',
              lineHeight: '1.15'
            }}>
              Why Microsoft – Compete Enablement for Partners
            </h1>
            
            {/* Subtitle */}
            <p style={{ 
              fontSize: '18px', 
              color: 'rgba(255,255,255,0.85)',
              marginBottom: '40px',
              lineHeight: '1.6'
            }}>
              <strong style={{ fontWeight: '700' }}>"Why Microsoft"</strong> provides authorized Microsoft partners with confidential compete assets including presentations, battle cards, solution guides, and training materials to effectively position Microsoft solutions against competition.
            </p>

            {/* Feature List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[
                'Battle cards & competitive analysis',
                'Sales presentations & enablement content',
                'Partner-exclusive resources',
                'Secure & confidential access'
              ].map((feature, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <CheckCircle size={20} style={{ color: '#C4E538', flexShrink: 0 }} />
                  <span style={{ fontSize: '16px', color: 'rgba(255,255,255,0.9)' }}>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Panel - Login Card */}
        <div style={{ 
          flex: '1 1 50%',
          backgroundColor: '#F8FAFB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px'
        }}>
          <div style={{ width: '100%', maxWidth: '420px' }}>
            {/* Login Card */}
            <div style={{ 
              backgroundColor: '#FFFFFF', 
              borderRadius: '16px', 
              boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
              overflow: 'hidden'
            }}>
              <div style={{ padding: '48px 40px' }}>
                {/* Welcome Message */}
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                  <h2 style={{ 
                    fontSize: '28px', 
                    fontWeight: '600', 
                    color: '#1F2937', 
                    marginBottom: '8px' 
                  }}>
                    Welcome
                  </h2>
                  <p style={{ fontSize: '15px', color: '#6B7280' }}>
                    {t('auth.ssoLogin')}
                  </p>
                </div>

                {/* SSO Login Button */}
                <button
                  onClick={handleSSOLogin}
                  data-testid="sso-login-button"
                  style={{
                    width: '100%',
                    padding: '16px 24px',
                    backgroundColor: '#1B5E5F',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    boxShadow: '0 2px 8px rgba(27,94,95,0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#0D4A4B';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#1B5E5F';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  {/* LevelUp Icon */}
                  <svg 
                    width="22" 
                    height="22" 
                    viewBox="0 0 32 32" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    style={{ pointerEvents: 'none' }}
                  >
                    <rect x="2" y="20" width="6" height="10" rx="1" fill="#C4E538"/>
                    <rect x="13" y="14" width="6" height="16" rx="1" fill="#C4E538"/>
                    <rect x="24" y="6" width="6" height="24" rx="1" fill="#C4E538"/>
                    <path d="M27 2L27 8M27 2L23 6M27 2L31 6" stroke="#C4E538" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span style={{ pointerEvents: 'none' }}>{t('auth.ssoLogin')}</span>
                </button>

                {/* Info Text */}
                <p style={{ 
                  textAlign: 'center', 
                  fontSize: '13px', 
                  color: '#9CA3AF',
                  marginTop: '24px',
                  lineHeight: '1.5'
                }}>
                  Use your LevelUp account credentials to access the "Why Microsoft" Portal.
                </p>
              </div>

              {/* Footer */}
              <div style={{ 
                backgroundColor: '#F9FAFB', 
                padding: '20px 40px',
                borderTop: '1px solid #E5E7EB'
              }}>
                <p style={{ 
                  fontSize: '12px', 
                  color: '#6B7280',
                  textAlign: 'center',
                  margin: 0
                }}>
                  Don't have a LevelUp account?{' '}
                  <button 
                    onClick={handleRegister}
                    style={{ 
                      color: '#1B5E5F', 
                      fontWeight: '600',
                      textDecoration: 'none',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      font: 'inherit'
                    }}
                  >
                    Register Now
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer style={{ 
        backgroundColor: '#1B5E5F', 
        padding: '16px 24px',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{ 
          maxWidth: '100%', 
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '24px',
          position: 'relative',
          zIndex: 1,
          flexWrap: 'wrap'
        }}>
          {/* Left Side - Branding */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', fontSize: '13px' }}>
            <span style={{ 
              fontSize: '13px', 
              fontWeight: 'normal',
              color: 'rgba(255,255,255,0.85)',
              margin: 0,
              lineHeight: '1'
            }}>
              Why Microsoft – Compete Enablement for Partners
            </span>
            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '13px' }}>|</span>
            <span style={{ 
              fontSize: '13px', 
              color: 'rgba(255,255,255,0.85)',
              fontWeight: 'normal',
              margin: 0
            }}>
              LevelUp
            </span>
            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '13px' }}>|</span>
            <span style={{ 
              fontSize: '13px', 
              color: 'rgba(255,255,255,0.85)',
              fontWeight: 'normal',
              margin: 0
            }}>
              Sponsored by Microsoft
            </span>
          </div>

          {/* Middle - Copyright */}
          <div style={{ 
            fontSize: '13px', 
            color: 'rgba(255,255,255,0.7)',
            textAlign: 'center'
          }}>
            © 2026 TechnoFocus Solutions. All rights reserved.
          </div>

          {/* Right Side - Links */}
          <div style={{ 
            display: 'flex', 
            gap: '20px',
            alignItems: 'center',
            fontSize: '13px'
          }}>
            <a 
              href="https://skillupwithlevelup.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: 'rgba(255,255,255,0.85)', 
                textDecoration: 'none',
                fontSize: '13px',
                fontWeight: 'normal',
                transition: 'color 0.2s',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#FFFFFF';
                const icon = e.currentTarget.querySelector('svg');
                if (icon) icon.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255,255,255,0.85)';
                const icon = e.currentTarget.querySelector('svg');
                if (icon) icon.style.opacity = '0.85';
              }}
            >
              LevelUp Home
              <ExternalLink size={14} style={{ opacity: 0.85, transition: 'opacity 0.2s' }} />
            </a>
            <a 
              href="https://technofocus.ai/privacy" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: 'rgba(255,255,255,0.85)', 
                textDecoration: 'none',
                fontSize: '13px',
                fontWeight: 'normal',
                transition: 'color 0.2s',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => e.target.style.color = '#FFFFFF'}
              onMouseLeave={(e) => e.target.style.color = 'rgba(255,255,255,0.85)'}
            >
              Privacy Statement
            </a>
          </div>
        </div>
      </footer>

      {/* Mobile Responsive Styles */}
      <style>{`
        @media (max-width: 900px) {
          header {
            padding: 16px 24px !important;
          }
          main {
            flex-direction: column !important;
          }
          main > div:first-child {
            padding: 40px 24px !important;
            flex: none !important;
          }
          main > div:first-child h1 {
            font-size: 28px !important;
          }
          main > div:last-child {
            padding: 40px 24px !important;
            flex: 1 !important;
          }
          footer > div {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 24px !important;
          }
          footer > div > div:last-child {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 16px !important;
          }
        }
      `}</style>
    </div>
  );
}

export default Login;
